import dayjs from 'dayjs'

import { featureFlags } from '@/lib/featureFlags'
import { isWithinSessionDay, resolveSessionPhase, SessionPhase } from '@/lib/sessionPhase'
import { getAvailableFeatureItemsByDependencies } from '@/lib/utils'

import { CadyMarket } from '@/types/CadyMarket'
import { ContextKey } from '@/types/ContextKey'

import {
  ProofTypeId,
  SessionTypes
} from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'
import type {
  CatalogCampaignStorefrontDto,
  CatalogPromotionStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import type { ContentfulEntry } from '#contentful/ContentfulEntrySys'
import type { ContentfulFeatureItemAlpha } from '#contentful/ContentfulFeatureItemAlpha'
import type { ContentfulFeatureItemBravo } from '#contentful/ContentfulFeatureItemBravo'

type HeroFeatureItemEntry = ContentfulEntry<ContentfulFeatureItemAlpha | ContentfulFeatureItemBravo>

// Qualifying "Traditional / Basic" senior session types for the Senior
// Preorder offer. Currently identical to `SENIOR_RETOUCHING_ELIGIBLE_SESSIONS`
// (`stores/commerce.ts`) but intentionally its own rule — preorder eligibility and
// retouching eligibility are distinct business concepts that merely coincide
// today, so they should be free to diverge without one silently changing the other.
export const SENIOR_PREORDER_ELIGIBLE_SESSION_TYPES: ReadonlySet<SessionTypes> = new Set([
  SessionTypes.LegacySession,
  SessionTypes.StandardSession,
  SessionTypes.SimpleSession,
  SessionTypes.Yearbook
])

// Proof types that qualify to SEE the preorder offer. `Prepaid` is excluded here
// because it routes to its own context; `Presentation` and anything else fall
// through to the dashboard.
export const SENIOR_PREORDER_ELIGIBLE_PROOF_TYPES: ReadonlySet<ProofTypeId> = new Set([
  ProofTypeId.Portfolio,
  ProofTypeId.ProofSheet,
  ProofTypeId.Online
])

// The four terminal contexts a Senior Preorder entry resolves to, driven
// entirely by the subject profile (no catalog fetch). See the route logic flow
// map (`docs/features/2026.07.28-senior-preorder/2026.07.23-001-preorder-route-logic-flow.md`).
export enum SeniorPreorderContext {
  // Eligible + session date today/future — show the offer page.
  Active = 'active',
  // Eligible tier, session date has passed and the session happened (not a
  // no-show; offer window closed) — route on to post-session senior ordering.
  Ended = 'ended',
  // Already prepaid — show the "Frame-Worthy" hero (or fall back).
  Prepaid = 'prepaid',
  // Ineligible session type / non-qualifying proof / past-date no-show / missing
  // data — redirect out.
  None = 'none'
}

// Where a non-renderable (or opt-out) state sends the customer.
export enum SeniorPreorderFallback {
  // Full reload to the CADY dashboard.
  Dashboard = 'dashboard',
  // The normal senior ordering flow (`/senior?s=TOKEN`) — used for post-session
  // ordering and as the "See Offers" target on the ended/prepaid heroes.
  SeniorOrdering = 'senior-ordering'
}

export interface SeniorPreorderResolution {
  context: SeniorPreorderContext
  // Where to send the customer when this context can't render (e.g. `none`, or
  // an ended/prepaid context whose hero content isn't authored). Consumed by the
  // data loader (`none` → immediate redirect) and, later, the view.
  fallback: SeniorPreorderFallback
}

export interface SeniorPreorderRouteInput {
  // The market the session was loaded under. Required because every other input
  // here comes from `SeniorDetail`, which is subject-level rather than
  // market-scoped — a subject loaded under another market can carry it. See the
  // market gate in `resolveSeniorPreorderRoute`.
  market: CadyMarket | null
  sessionType: SessionTypes | null
  proofType: ProofTypeId | null
  // The subject's session phase (pre / post / did-not-occur), or null when the
  // session date is unknown. Computed by the shared `resolveSessionPhase` primitive
  // (ADR 2026.07.27-001) — this resolver *consumes* the phase, never recomputes it.
  sessionPhase: SessionPhase | null
}

// Resolve the Senior Preorder context from the subject's eligibility + session
// phase alone. Pure and deterministic given its inputs, so it can be unit-tested
// and reasoned about in isolation. The eligibility scenarios below encode the
// handoff's scenario table; note the "completed session on a different token" and
// "Elite/Most Popular → dashboard" rows are handled UPSTREAM by the token preflight
// (SwapToken + Online-proof gate), so they don't appear here.
export function resolveSeniorPreorderRoute(
  input: SeniorPreorderRouteInput
): SeniorPreorderResolution {
  // Feature gate — FIRST, ahead of even the market gate. This is the single
  // choke point for the whole feature: every consumer (route redirects,
  // `commerce.ts` step shaping, the checkout step counter) keys off the
  // resolved context, so returning `None` + `Dashboard`
  // here reverts all of them at once. Deliberately NOT gated at the route
  // registration: unregistering the route would drop `/senior/preorder?s=TOKEN`
  // into the catch-all 404 page, whereas the intended pre-launch behavior is a
  // forward to the CADY dashboard.
  //
  // `resolveSeniorPreorderRouteIgnoringFeatureFlag` is the ungated form, used
  // only by the `?preview` dry-run.
  if (!featureFlags.seniorPreorder) {
    return {
      context: SeniorPreorderContext.None,
      fallback: SeniorPreorderFallback.Dashboard
    }
  }
  return resolveSeniorPreorderRouteIgnoringFeatureFlag(input)
}

// The resolver proper, with no feature-flag check. Exported for the `?preview`
// dry-run, which must report how a token WOULD route once the feature is on —
// reporting `none`/Dashboard for every token pre-launch would make the
// diagnostic useless exactly when it's most needed. Prefer
// `resolveSeniorPreorderRoute` everywhere else.
export function resolveSeniorPreorderRouteIgnoringFeatureFlag(
  input: SeniorPreorderRouteInput
): SeniorPreorderResolution {
  const { market, sessionType, proofType, sessionPhase } = input

  const dashboard: SeniorPreorderResolution = {
    context: SeniorPreorderContext.None,
    fallback: SeniorPreorderFallback.Dashboard
  }

  // Market gate — FIRST, because every other input is read off `SeniorDetail`,
  // which is subject-level, not market-scoped. Without this, a subject loaded
  // under another market that happens to carry an eligible `SeniorDetail` would
  // resolve `active` and silently reshape THAT market's flow: `commerce.ts` keys
  // its "drop the full-page RETOUCHING step" and "relabel Gallery → Order for
  // {name}" adjustments off this context, and `CheckoutView` hides the step
  // counter on it. Those consumers are app-wide, not preorder-route-scoped
  // (deliberately — the preorder flow must shape Checkout's step nav too), so the
  // scoping has to live here.
  if (market !== CadyMarket.Senior) {
    return dashboard
  }

  // Ineligible / missing session type → dashboard, regardless of phase.
  if (sessionType === null || !SENIOR_PREORDER_ELIGIBLE_SESSION_TYPES.has(sessionType)) {
    return dashboard
  }

  // Already prepaid → its own surface.
  if (proofType === ProofTypeId.Prepaid) {
    return {
      context: SeniorPreorderContext.Prepaid,
      fallback: SeniorPreorderFallback.SeniorOrdering
    }
  }

  // Non-qualifying proof type (Presentation / missing / other) → dashboard.
  // (Presentation is also caught upstream by `redirectPresentationSenior`; kept
  // here so the resolver is correct in isolation.)
  if (proofType === null || !SENIOR_PREORDER_ELIGIBLE_PROOF_TYPES.has(proofType)) {
    return dashboard
  }

  // Eligible subject: the terminal context turns on the session phase. The
  // campaign's end-of-day window (MKT-001) is the authoritative *offer* gate
  // server-side; the phase mirrors it for routing.
  //   PreSession  → active (show the offer page)
  //   PostSession → ended (session happened → post-session ordering)
  //   DidNotOccur → dashboard (no-show; the handoff's "if not complete → Dashboard")
  //   null        → dashboard (unknown session date — no basis to show the offer)
  switch (sessionPhase) {
    case SessionPhase.PreSession:
      return {
        context: SeniorPreorderContext.Active,
        fallback: SeniorPreorderFallback.Dashboard
      }
    case SessionPhase.PostSession:
      return {
        context: SeniorPreorderContext.Ended,
        fallback: SeniorPreorderFallback.SeniorOrdering
      }
    case SessionPhase.DidNotOccur:
    case null:
    default:
      return dashboard
  }
}

// Leading path segment of the normal senior catalog path, and its preorder twin.
const SENIOR_CATALOG_SEGMENT = 'senior'
const SENIOR_PREORDER_CATALOG_SEGMENT = 'senior-preorder'

// Derive the preorder catalog path from the backend-delivered senior `CatalogPath`
// by swapping the leading `senior` segment for `senior-preorder`
// (`senior/2026/13/2/fl/12345` → `senior-preorder/2026/13/2/fl/12345`). See
// ADR 2026.07.23-001 — a distinct top-level segment guarantees Sunny16's
// longest-prefix `FindCatalog` can't cross-link preorder and post-session senior
// catalogs in either direction. Transform-not-rebuild: it rides on the
// server-built path, so it stays in sync with `CoreApiShared.BuildCatalogPath`
// and needs no `SessionSubTypeId`. Returns null when the input isn't a senior
// path (leading segment ≠ `senior`), so the caller can fall back rather than
// address a bogus catalog.
export function buildSeniorPreorderCatalogPath(
  deliveredCatalogPath: string | null | undefined
): string | null {
  if (!deliveredCatalogPath) {
    return null
  }
  const segments = deliveredCatalogPath.split('/')
  if (segments[0]?.toLowerCase() !== SENIOR_CATALOG_SEGMENT) {
    return null
  }
  segments[0] = SENIOR_PREORDER_CATALOG_SEGMENT
  return segments.join('/')
}

// The senior ordering deep link carrying the session token (`/senior?s=TOKEN`) —
// the Senior Preorder fallback target for the `ended`/`prepaid` contexts and
// the "See Offers" CTA on those heroes. Shared by the data loader
// (`forwardToSeniorOrdering`) and `FeatureItemCta` so both build the same URL.
export function seniorOrderingUrl(sessionToken: string | null): string {
  const url = new URL('/senior', window.location.origin)
  if (sessionToken) {
    url.searchParams.set('s', sessionToken)
  }
  return url.toString()
}
// The `dependsOnContext` gate key (see `types/ContextKey.ts`) for each resolved
// context, so `PreorderView` can feed it to `getAvailableFeatureItemsByDependencies`
// and surface the matching authored hero. `None` has no hero (it's redirected).
// Exhaustive over `SeniorPreorderContext` — adding a context forces a mapping.
const CONTEXT_KEY_BY_CONTEXT: Record<SeniorPreorderContext, ContextKey | null> = {
  [SeniorPreorderContext.Active]: ContextKey.SeniorPreorderActive,
  [SeniorPreorderContext.Ended]: ContextKey.SeniorPreorderEnded,
  [SeniorPreorderContext.Prepaid]: ContextKey.SeniorPreorderPrepaid,
  [SeniorPreorderContext.None]: null
}

export function seniorPreorderContextKey(context: SeniorPreorderContext): ContextKey | null {
  return CONTEXT_KEY_BY_CONTEXT[context]
}

// The authored `heroFeatureItems` that should render for a resolved context,
// filtered by the existing `dependsOnContext` gate. Returns null when none
// match (→ the loader falls back; the view omits the hero). Shared by the data
// loader (fallback decision) and `PreorderView` (render) so the two agree on
// "is there a hero for this context?".
export function resolvePreorderHeroItems(
  heroFeatureItems: HeroFeatureItemEntry[] | undefined,
  context: SeniorPreorderContext,
  activeCampaigns: CatalogCampaignStorefrontDto[] | null | undefined,
  availablePromotions: CatalogPromotionStorefrontDto[] | null | undefined
): HeroFeatureItemEntry[] | null {
  if (!heroFeatureItems || heroFeatureItems.length === 0) {
    return null
  }

  const contextKey = seniorPreorderContextKey(context)
  const activeContexts: ContextKey[] = contextKey ? [contextKey] : []

  const available = getAvailableFeatureItemsByDependencies(
    heroFeatureItems,
    activeCampaigns,
    availablePromotions,
    activeContexts
  )
  const displayable = (available ?? []).filter((item) => item.fields !== undefined)
  return displayable.length > 0 ? displayable : null
}

// --- Dev-only dry-run preview -------------------------------------------------
// A read-only summary of what `resolveSeniorPreorderRoute` would do with a
// token — for the `?preview` mode on `/senior/preorder` (dev server only). Adds
// display fields (enum labels, the computed preorder catalog path, a plain-English
// intended action) on top of the raw resolution, WITHOUT any redirect or catalog
// fetch. Rendered by `PreorderView` and logged to console; see the session token
// catalog (`docs/features/2026.07.28-senior-preorder/2026.07.24-001-...`).

export interface SeniorPreorderPreview {
  context: SeniorPreorderContext
  fallback: SeniorPreorderFallback
  sessionType: SessionTypes | null
  sessionTypeLabel: string
  proofType: ProofTypeId | null
  proofTypeLabel: string
  sessionDateUtc: string | null
  offerWindowOpen: boolean | null
  sessionStatusId: number | null
  preorderCatalogPath: string | null
  intendedAction: string
  // Whether the Senior Preorder feature flag is on. The context/fallback above
  // are computed as if it were on (see `buildSeniorPreorderPreview`), so this is
  // what tells you whether the reported routing is what a real visit would do.
  featureEnabled: boolean
}

export interface SeniorPreorderPreviewInput {
  market: CadyMarket | null
  sessionType: SessionTypes | null
  proofType: ProofTypeId | null
  sessionDateUtc: Date | string | null
  sessionStatusId: number | null
  // The backend-delivered `CatalogPath`, used to compute the preorder path.
  deliveredCatalogPath: string | null | undefined
  now: Date
}

// "<value> <NAME>" for a numeric enum member, "—" for null. Falls back to the raw
// value if there's no reverse-mapped name (e.g. an unexpected id, or a string enum).
function numericEnumLabel(enumObj: Record<number, string>, value: number | null): string {
  if (value === null) {
    return '—'
  }
  const name = enumObj[value]
  return name !== undefined ? `${value} ${name}` : String(value)
}

function describePreorderIntent(
  context: SeniorPreorderContext,
  fallback: SeniorPreorderFallback,
  preorderCatalogPath: string | null,
  featureEnabled: boolean
): string {
  // With the feature off nothing below actually happens — say so up front, then
  // still report the would-be routing so the panel stays useful pre-launch.
  if (!featureEnabled) {
    return `FEATURE OFF (VITE_FEATURE_SENIOR_PREORDER) — this token forwards to the CADY dashboard. Once enabled it would: ${describePreorderIntent(context, fallback, preorderCatalogPath, true)}`
  }
  switch (context) {
    case SeniorPreorderContext.Active:
      return `Fetch the preorder catalog at "${preorderCatalogPath ?? '(non-senior path — no preorder path)'}" and render the active offer page (falls back if the catalog is empty and no active hero is authored).`
    case SeniorPreorderContext.Ended:
      return 'Render the "ended" hero if authored, else forward to /senior?s=TOKEN (post-session ordering).'
    case SeniorPreorderContext.Prepaid:
      return 'Render the "prepaid" (Frame-Worthy) hero if authored, else forward to /senior?s=TOKEN.'
    case SeniorPreorderContext.None:
      return fallback === SeniorPreorderFallback.SeniorOrdering
        ? 'Forward to /senior?s=TOKEN (post-session ordering).'
        : 'Redirect to the CADY dashboard (full reload).'
  }
}

export function buildSeniorPreorderPreview(
  input: SeniorPreorderPreviewInput
): SeniorPreorderPreview {
  // Deliberately the flag-IGNORING resolver: the dry-run's job is to explain how
  // this token routes on its own merits. With the feature off, the gated resolver
  // returns `none`/Dashboard for every token, which would make the panel report
  // the flag rather than the subject — useless for verifying data and content
  // ahead of launch. `featureEnabled` below reports the flag separately.
  const { context, fallback } = resolveSeniorPreorderRouteIgnoringFeatureFlag({
    market: input.market,
    sessionType: input.sessionType,
    proofType: input.proofType,
    sessionPhase: resolveSessionPhase({
      sessionDateUtc: input.sessionDateUtc,
      sessionStatusId: input.sessionStatusId,
      now: input.now
    })
  })
  const preorderCatalogPath = buildSeniorPreorderCatalogPath(input.deliveredCatalogPath)
  return {
    featureEnabled: featureFlags.seniorPreorder,
    context,
    fallback,
    sessionType: input.sessionType,
    sessionTypeLabel: numericEnumLabel(
      SessionTypes as unknown as Record<number, string>,
      input.sessionType
    ),
    proofType: input.proofType,
    proofTypeLabel: numericEnumLabel(
      ProofTypeId as unknown as Record<number, string>,
      input.proofType
    ),
    sessionDateUtc:
      input.sessionDateUtc === null ? null : dayjs(input.sessionDateUtc).toISOString(),
    offerWindowOpen: isWithinSessionDay(input.sessionDateUtc, input.now),
    sessionStatusId: input.sessionStatusId,
    preorderCatalogPath,
    intendedAction: describePreorderIntent(
      context,
      fallback,
      preorderCatalogPath,
      featureFlags.seniorPreorder
    )
  }
}
